<template>
  <div>
    <!-- Dialog principal de pendências -->
    <v-dialog 
      v-model="dialogVisible" 
      fullscreen
      @update:model-value="handleDialogChange"
    >
      <v-card class="d-flex flex-column" :height="mobile ? '100vh' : 'auto'">
        <!-- Header fixo com design melhorado -->
        <v-card-title class="bg-gradient-dark text-white sticky-top pa-0">
          <!-- Título Principal -->
          <div class="d-flex align-center w-100 pa-4 pb-2">
            <v-icon class="mr-3" size="large">mdi-alert-circle-outline</v-icon>
            <div class="flex-grow-1">
              <div class="text-h5 font-weight-bold">{{ titulo }}</div>
              <div class="text-subtitle-1 opacity-90">
                {{ totalPendenciasFiltradas }} de {{ totalPendenciasAtual }} movimentações
              </div>
            </div>
            <v-btn 
              icon 
              variant="text" 
              @click="fecharDialog"
              class="ml-2"
              size="large"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>

          <!-- ✅ ÁREA DE FILTRO SIMPLIFICADA -->
          <div class="pa-4 pt-0">
            <v-row align="center" no-gutters>
              <v-col cols="12" md="8" class="pr-md-3">
                <!-- ✅ CAMPO DE TEXTO SIMPLES PARA FILTRO -->
                <v-text-field
                  v-model="filtroTexto"
                  label="Filtrar por Ordem SAP, Cliente ou Modelo"
                  placeholder="Digite: 25933, nome do cliente ou modelo..."
                  prepend-inner-icon="mdi-magnify"
                  variant="outlined"
                  density="compact"
                  clearable
                  hide-details
                  class="filtro-text-header"
                  bg-color="rgba(255,255,255,0.1)"
                  color="white"
                  @input="filtrarPendencias"
                  @click:clear="limparFiltro"
                >
                  <!-- ✅ ÍCONE DE LOADING -->
                  <template v-slot:append-inner v-if="loadingFiltro">
                    <v-progress-circular
                      indeterminate
                      color="white"
                      size="20"
                      width="2"
                    ></v-progress-circular>
                  </template>
                </v-text-field>
              </v-col>
              
              <v-col cols="12" md="4" class="mt-2 mt-md-0">
                <div class="d-flex align-center justify-end flex-wrap gap-2">
                  <!-- ✅ CHIP DE FILTRO ATIVO -->
                  <v-chip 
                    v-if="filtroTexto" 
                    color="white" 
                    text-color="grey-darken-3"
                    variant="flat"
                    size="small"
                    class="font-weight-bold filtro-ativo-chip"
                  >
                    <v-icon start size="small" color="grey-darken-2">mdi-filter</v-icon>
                    "{{ filtroTexto }}"
                    <span class="ml-1 text-success">({{ totalPendenciasFiltradas }})</span>
                  </v-chip>
                  
                  <!-- Botão limpar filtro -->
                  <v-btn
                    v-if="filtroTexto"
                    color="white"
                    variant="outlined"
                    size="small"
                    @click="limparFiltro"
                    class="text-white border-white limpar-filtro-btn"
                  >
                    <v-icon start size="small">mdi-filter-remove</v-icon>
                    Limpar
                  </v-btn>
                  
                  <!-- Info de ordens -->
                  <v-chip 
                    color="rgba(255,255,255,0.15)" 
                    text-color="white"
                    variant="flat"
                    size="small"
                    class="font-weight-medium info-chip"
                  >
                    <v-icon start size="small">mdi-information</v-icon>
                    {{ Object.keys(pendenciasAgrupadasOrdenadas).length }} ordem(ns)
                  </v-chip>
                </div>
              </v-col>
            </v-row>

            <!-- ✅ DICAS DE BUSCA -->
            <div v-if="!filtroTexto" class="mt-2 text-center">
              <div class="text-caption opacity-70">
                💡 Digite o número da ordem SAP, nome do cliente ou modelo para filtrar
              </div>
            </div>

            <!-- ✅ RESULTADOS DA BUSCA -->
            <div v-if="filtroTexto && totalPendenciasFiltradas === 0" class="mt-2 text-center">
              <v-chip color="orange" text-color="white" size="small">
                <v-icon start size="small">mdi-alert</v-icon>
                Nenhum resultado para "{{ filtroTexto }}"
              </v-chip>
            </div>
          </div>
        </v-card-title>

        <!-- ✅ CONTEÚDO SCROLLÁVEL COM LAYOUT CONDICIONAL -->
        <v-card-text class="flex-grow-1 pa-0" style="overflow-y: auto;">
          <div v-if="pendenciasAgrupadasOrdenadas && Object.keys(pendenciasAgrupadasOrdenadas).length > 0" class="pa-4">
            
            <!-- Card para cada ordem -->
            <v-card 
              v-for="(pendenciasOrdem, ordemSap) in pendenciasAgrupadasOrdenadas" 
              :key="ordemSap"
              class="mb-6 elevation-4"
              rounded="lg"
            >
              <!-- Cabeçalho da Ordem -->
              <v-card-title class="bg-gradient-primary text-white pa-4">
                <div class="d-flex align-center w-100">
                  <v-avatar color="white" class="mr-3">
                    <v-icon color="primary">mdi-clipboard-list-outline</v-icon>
                  </v-avatar>
                  
                  <div class="flex-grow-1">
                    <div class="text-h6 font-weight-bold mb-1">
                      <v-icon size="small" class="mr-1">mdi-clipboard-text</v-icon>
                      Ordem SAP: <span class="highlight-search">{{ ordemSap }}</span>
                    </div>
                    <div class="text-body-2 opacity-90">
                      <v-icon size="small" class="mr-1">mdi-domain</v-icon>
                      <span class="highlight-search">{{ pendenciasOrdem[0]?.cliente }}</span>
                    </div>
                    <div class="text-body-2 opacity-80">
                      <v-icon size="small" class="mr-1">mdi-tshirt-crew-outline</v-icon>
                      <span class="highlight-search">{{ pendenciasOrdem[0]?.modelo }}</span>
                    </div>
                  </div>
                  
                  <!-- Área dos botões -->
                  <div class="d-flex align-center gap-3">
                    <v-chip 
                      color="white" 
                      text-color="primary" 
                      size="large"
                      class="font-weight-bold"
                    >
                      <v-icon start>mdi-counter</v-icon>
                      {{ pendenciasOrdem.length }} pendência(s)
                    </v-chip>
                    
                    <!-- Botão Autorizar Todas -->
                    <v-chip 
                      @click="confirmarAutorizacaoOrdem(ordemSap, pendenciasOrdem)"
                      color="gradient-success"
                      :loading="loadingAutorizacaoOrdem === ordemSap"
                      variant="flat"
                      size="large"
                      class="font-weight-bold text-white chip-processar ml-5"
                      :ripple="true"
                    >
                      <v-icon start>mdi-check-all</v-icon>
                      Processar Todas
                    </v-chip>
                  </div>
                </div>
              </v-card-title>

              <!-- ✅ TABELA APENAS PARA DESKTOP -->
              <v-card-text v-if="!mobile" class="pa-0">
                <v-table density="comfortable" class="elevation-0">
                  <thead>
                    <tr class="bg-grey-lighten-4">
                      <th class="text-center font-weight-bold">ID</th>
                      <th class="text-center font-weight-bold">Movimentação</th>
                      <th class="text-center font-weight-bold">Item</th>
                      <th class="text-center font-weight-bold">Qtd</th>
                      <th class="text-center font-weight-bold">Data</th>
                      <th class="text-center font-weight-bold">Observação</th>
                      <th class="text-center font-weight-bold">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="pendencia in pendenciasOrdem" :key="pendencia.id" class="hover-row">
                      <!-- ID da Movimentação -->
                      <td class="text-center">
                        <v-chip color="blue-darken-1" variant="flat" size="small">
                          <v-icon start size="small">mdi-identifier</v-icon>
                          {{ pendencia.id }}
                        </v-chip>
                      </td>

                      <!-- Movimentação (De → Para) -->
                      <td class="text-center">
                        <div class="d-flex align-center justify-center">
                          <v-chip size="small" color="purple-darken-1" variant="outlined" class="mr-1">
                            {{ pendencia.movimentacao?.de || 'N/A' }}
                          </v-chip>
                          <v-icon size="small" class="mx-2" color="grey-darken-1">mdi-arrow-right-bold</v-icon>
                          <v-chip size="small" color="orange-darken-1" variant="outlined" class="ml-1">
                            {{ pendencia.movimentacao?.para || 'N/A' }}
                          </v-chip>
                        </div>
                      </td>

                      <!-- Item (Parte + Tamanho) -->
                      <td class="text-center">
                        <div class="text-body-2">
                          <div class="font-weight-bold text-primary">{{ pendencia.item?.parte || 'N/A' }}</div>
                          <v-chip size="x-small" color="grey-darken-1" variant="outlined">
                            {{ pendencia.item?.tamanho || 'N/A' }}
                          </v-chip>
                        </div>
                      </td>

                      <!-- Quantidade -->
                      <td class="text-center">
                        <v-chip color="green-darken-1" variant="flat" size="small">
                          <v-icon start size="small">mdi-counter</v-icon>
                          {{ pendencia.item?.quantidade || 0 }}
                        </v-chip>
                      </td>

                      <!-- Data -->
                      <td class="text-center">
                        <div class="text-body-2">
                          <div class="font-weight-medium">{{ pendencia.tempo?.data_formatada || 'N/A' }}</div>
                          <v-chip 
                            size="x-small" 
                            :color="pendencia.tempo?.dias_pendente > 2 ? 'red-lighten-1' : 'grey-lighten-1'"
                            variant="flat"
                          >
                            {{ pendencia.tempo?.dias_pendente || 0 }} dia(s)
                          </v-chip>
                        </div>
                      </td>

                      <!-- Observação -->
                      <td class="text-center" style="max-width: 150px;">
                        <v-tooltip v-if="pendencia.observacao" :text="pendencia.observacao">
                          <template v-slot:activator="{ props }">
                            <v-chip 
                              v-bind="props"
                              size="small" 
                              color="grey-darken-1" 
                              variant="outlined"
                              class="text-truncate"
                            >
                              <v-icon start size="small">mdi-note-text</v-icon>
                              {{ pendencia.observacao.substring(0, 10) }}...
                            </v-chip>
                          </template>
                        </v-tooltip>
                        <v-chip v-else size="small" color="grey-lighten-2" variant="outlined">
                          <v-icon size="small">mdi-note-off</v-icon>
                        </v-chip>
                      </td>

                      <!-- Ações -->
                      <td class="text-center">
                        <v-btn 
                          color="success" 
                          size="small" 
                          variant="flat"
                          rounded="lg"
                          :loading="loadingAutorizacaoLocal === pendencia.id"
                          @click="confirmarAutorizacao(pendencia)"
                        >
                          <v-icon start size="small">mdi-check-bold</v-icon>
                          Autorizar
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </v-table>
              </v-card-text>

              <!-- ✅ CARDS PARA MOBILE (FULLSCREEN) -->
              <v-card-text v-else class="pa-2">
                <!-- Botão Autorizar Todas - Mobile -->
                <div class="pa-3 bg-grey-lighten-5 mb-3 rounded">
                  <v-btn
                    color="success"
                    variant="flat"
                    block
                    rounded="lg"
                    :loading="loadingAutorizacaoOrdem === ordemSap"
                    @click="confirmarAutorizacaoOrdem(ordemSap, pendenciasOrdem)"
                  >
                    <v-icon start>mdi-check-all</v-icon>
                    Autorizar Todas as {{ pendenciasOrdem.length }} Pendências
                  </v-btn>
                </div>

                <v-card 
                  v-for="pendencia in pendenciasOrdem" 
                  :key="pendencia.id"
                  class="mb-3 elevation-2"
                  rounded="lg"
                >
                  <v-card-text class="pa-3">
                    <!-- Header do item mobile -->
                    <div class="d-flex align-center mb-2">
                      <v-chip color="blue-darken-1" size="small" class="mr-2">
                        ID: {{ pendencia.id }}
                      </v-chip>
                      <v-spacer></v-spacer>
                      <v-chip 
                        :color="pendencia.tempo?.dias_pendente > 2 ? 'red-lighten-1' : 'grey-lighten-1'"
                        size="small"
                      >
                        {{ pendencia.tempo?.dias_pendente || 0 }} dia(s)
                      </v-chip>
                    </div>

                    <!-- Movimentação -->
                    <div class="mb-2">
                      <div class="text-caption text-grey mb-1">MOVIMENTAÇÃO</div>
                      <div class="d-flex align-center">
                        <v-chip size="small" color="purple-darken-1" variant="outlined">
                          {{ pendencia.movimentacao?.de || 'N/A' }}
                        </v-chip>
                        <v-icon class="mx-2" size="small">mdi-arrow-right-bold</v-icon>
                        <v-chip size="small" color="orange-darken-1" variant="outlined">
                          {{ pendencia.movimentacao?.para || 'N/A' }}
                        </v-chip>
                      </div>
                    </div>

                    <!-- Item e Quantidade -->
                    <div class="mb-2">
                      <div class="text-caption text-grey mb-1">ITEM</div>
                      <div class="d-flex align-center">
                        <div class="flex-grow-1">
                          <div class="font-weight-bold">{{ pendencia.item?.parte || 'N/A' }}</div>
                          <div class="text-body-2 text-grey">Tamanho: {{ pendencia.item?.tamanho || 'N/A' }}</div>
                        </div>
                        <v-chip color="green-darken-1" size="small">
                          {{ pendencia.item?.quantidade || 0 }} pcs
                        </v-chip>
                      </div>
                    </div>

                    <!-- Data e Observação -->
                    <div class="mb-3">
                      <div class="text-caption text-grey mb-1">DATA</div>
                      <div class="text-body-2">{{ pendencia.tempo?.data_formatada || 'N/A' }}</div>
                      
                      <div v-if="pendencia.observacao" class="mt-2">
                        <div class="text-caption text-grey mb-1">OBSERVAÇÃO</div>
                        <div class="text-body-2">{{ pendencia.observacao }}</div>
                      </div>
                    </div>

                    <!-- Botão de ação individual -->
                    <v-btn 
                      color="success" 
                      block
                      variant="flat"
                      rounded="lg"
                      :loading="loadingAutorizacaoLocal === pendencia.id"
                      @click="confirmarAutorizacao(pendencia)"
                    >
                      <v-icon start>mdi-check-bold</v-icon>
                      Autorizar Individual
                    </v-btn>
                  </v-card-text>
                </v-card>
              </v-card-text>
            </v-card>

          </div>
          
          <!-- Mensagem quando não há pendências -->
          <div v-else class="text-center pa-8">
            <v-avatar size="120" color="blue-grey-lighten-4" class="mb-4">
              <v-icon size="60" color="blue-grey-lighten-1">
                {{ filtroTexto ? 'mdi-filter-remove' : 'mdi-clipboard-check-outline' }}
              </v-icon>
            </v-avatar>
            <h3 class="text-h5 mb-2 text-blue-grey-darken-1">
              {{ filtroTexto ? `Nenhuma pendência encontrada para "${filtroTexto}"` : mensagemVazia }}
            </h3>
            <p class="text-body-1 text-grey">
              {{ filtroTexto ? 'Tente outro termo de busca ou limpe o filtro.' : descricaoVazia }}
            </p>
            <v-btn 
              v-if="filtroTexto" 
              color="primary" 
              variant="outlined" 
              class="mt-4"
              @click="limparFiltro"
            >
              <v-icon start>mdi-filter-remove</v-icon>
              Limpar Filtro
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Restante dos dialogs mantém igual... -->
    <!-- Dialog de Confirmação Individual -->
    <v-dialog v-model="dialogConfirmarAutorizacao" max-width="600">
      <v-card>
        <v-card-title class="bg-warning text-white d-flex align-center">
          <v-icon class="mr-2">mdi-help-circle</v-icon>
          Confirmar Autorização Individual
        </v-card-title>

        <v-card-text class="pa-4">
          <p class="text-h6 mb-4">Deseja realmente autorizar esta movimentação?</p>

          <!-- ✅ CARD COM DADOS DA PENDÊNCIA MELHORADO -->
          <v-card v-if="pendenciaSelecionada" variant="outlined" class="mb-4">
            <v-card-text class="pa-4">
              <v-row dense>
                <!-- ID e Ordem -->
                <v-col cols="12" md="6">
                  <div class="info-group">
                    <div class="info-label">ID da Movimentação</div>
                    <v-chip color="blue-darken-1" variant="flat" size="small">
                      <v-icon start size="small">mdi-identifier</v-icon>
                      {{ pendenciaSelecionada.id }}
                    </v-chip>
                  </div>
                </v-col>
                
                <v-col cols="12" md="6">
                  <div class="info-group">
                    <div class="info-label">Ordem SAP</div>
                    <v-chip color="orange-darken-1" variant="flat" size="small">
                      <v-icon start size="small">mdi-clipboard-text</v-icon>
                      {{ pendenciaSelecionada.ordem_producao?.id_ordem_sap || 'N/A' }}
                    </v-chip>
                  </div>
                </v-col>

                <!-- Cliente -->
                <v-col cols="12">
                  <div class="info-group">
                    <div class="info-label">Cliente</div>
                    <div class="info-value">
                      <v-icon color="primary" size="small" class="mr-2">mdi-domain</v-icon>
                      {{ pendenciaSelecionada.ordem_producao?.detalhes?.cliente?.nome || 'N/A' }}
                    </div>
                  </div>
                </v-col>

                <!-- Movimentação -->
                <v-col cols="12">
                  <div class="info-group">
                    <div class="info-label">Movimentação</div>
                    <div class="d-flex align-center">
                      <v-chip size="small" color="purple-darken-1" variant="outlined" class="mr-2">
                        {{ pendenciaSelecionada.setor_origem?.nome || 'N/A' }}
                      </v-chip>
                      <v-icon size="small" color="grey-darken-1">mdi-arrow-right-bold</v-icon>
                      <v-chip size="small" color="orange-darken-1" variant="outlined" class="ml-2">
                        {{ pendenciaSelecionada.setor_destino?.nome || 'N/A' }}
                      </v-chip>
                    </div>
                  </div>
                </v-col>

                <!-- Item -->
                <v-col cols="12" md="4">
                  <div class="info-group">
                    <div class="info-label">Parte</div>
                    <div class="info-value font-weight-bold text-primary">
                      <v-icon color="primary" size="small" class="mr-2">mdi-puzzle</v-icon>
                      {{ pendenciaSelecionada.parte || 'N/A' }}
                    </div>
                  </div>
                </v-col>

                <v-col cols="12" md="4">
                  <div class="info-group">
                    <div class="info-label">Tamanho</div>
                    <v-chip color="grey-darken-1" variant="outlined" size="small">
                      {{ pendenciaSelecionada.tamanho || 'N/A' }}
                    </v-chip>
                  </div>
                </v-col>

                <v-col cols="12" md="4">
                  <div class="info-group">
                    <div class="info-label">Quantidade</div>
                    <v-chip color="green-darken-1" variant="flat" size="small">
                      <v-icon start size="small">mdi-counter</v-icon>
                      {{ pendenciaSelecionada.quantidade || 0 }}
                    </v-chip>
                  </div>
                </v-col>

                <!-- Data -->
                <v-col cols="12" md="6">
                  <div class="info-group">
                    <div class="info-label">Data de Registro</div>
                    <v-chip color="indigo" variant="flat" size="small">
                      <v-icon start size="small">mdi-calendar</v-icon>
                      {{ formatarDataPendencia(pendenciaSelecionada.data_registro) }}
                    </v-chip>
                  </div>
                </v-col>

                <!-- Observação (se houver) -->
                <v-col cols="12" v-if="pendenciaSelecionada.observacao">
                  <div class="info-group">
                    <div class="info-label">Observação</div>
                    <div class="info-value">
                      <v-icon color="info" size="small" class="mr-2">mdi-note-text</v-icon>
                      {{ pendenciaSelecionada.observacao }}
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn 
            color="grey" 
            variant="outlined" 
            @click="dialogConfirmarAutorizacao = false"
          >
            <v-icon start>mdi-close</v-icon>
            Cancelar
          </v-btn>
          <v-btn 
            color="success" 
            variant="flat"
            :loading="loadingAutorizacaoLocal"
            @click="executarAutorizacao"
          >
            <v-icon start>mdi-check-bold</v-icon>
            Confirmar Autorização
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog de Confirmação por Ordem -->
    <v-dialog v-model="dialogConfirmarAutorizacaoOrdem" max-width="700">
      <v-card>
        <v-card-title class="bg-success text-white d-flex align-center">
          <v-icon class="mr-2">mdi-check-all</v-icon>
          Confirmar Autorização em Lote
        </v-card-title>

        <v-card-text class="pa-4">
          <div class="text-h6 mb-4 d-flex align-center">
            <v-icon class="mr-2" color="warning">mdi-alert</v-icon>
            Deseja autorizar TODAS as pendências desta ordem?
          </div>

          <v-card variant="outlined" class="mb-4" v-if="ordemSelecionadaParaAutorizar">
            <v-card-text>
              <v-row dense>
                <v-col cols="12" md="6">
                  <div class="text-caption text-grey">ORDEM SAP</div>
                  <div class="text-h6 font-weight-bold text-primary">{{ ordemSelecionadaParaAutorizar }}</div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="text-caption text-grey">TOTAL DE PENDÊNCIAS</div>
                  <div class="text-h6 font-weight-bold text-success">{{ pendenciasOrdemSelecionada.length }}</div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="text-caption text-grey">CLIENTE</div>
                  <div class="text-body-1">{{ pendenciasOrdemSelecionada[0]?.cliente || 'N/A' }}</div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="text-caption text-grey">MODELO</div>
                  <div class="text-body-1">{{ pendenciasOrdemSelecionada[0]?.modelo || 'N/A' }}</div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <v-alert type="info" variant="tonal" class="mb-4">
            <div class="text-body-2">
              <strong>Atenção:</strong> Esta ação irá autorizar todas as {{ pendenciasOrdemSelecionada.length }} movimentações desta ordem de uma só vez.
            </div>
          </v-alert>

          <!-- Lista resumida das pendências -->
          <v-expansion-panels variant="accordion" class="mb-4">
            <v-expansion-panel>
              <v-expansion-panel-title>
                <v-icon class="mr-2">mdi-eye</v-icon>
                Ver detalhes das {{ pendenciasOrdemSelecionada.length }} pendências
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-list density="compact">
                  <v-list-item 
                    v-for="pendencia in pendenciasOrdemSelecionada" 
                    :key="pendencia.id"
                    class="px-0"
                  >
                    <template v-slot:prepend>
                      <v-chip size="x-small" color="blue">{{ pendencia.id }}</v-chip>
                    </template>
                    <v-list-item-title class="text-body-2">
                      {{ pendencia.item?.parte || 'N/A' }} - {{ pendencia.item?.tamanho || 'N/A' }}
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      {{ pendencia.movimentacao?.de || 'N/A' }} → {{ pendencia.movimentacao?.para || 'N/A' }}
                    </v-list-item-subtitle>
                    <template v-slot:append>
                      <v-chip size="x-small" color="green">{{ pendencia.item?.quantidade || 0 }}</v-chip>
                    </template>
                  </v-list-item>
                </v-list>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn 
            color="grey" 
            variant="outlined" 
            @click="dialogConfirmarAutorizacaoOrdem = false"
          >
            <v-icon start>mdi-close</v-icon>
            Cancelar
          </v-btn>
          <v-btn 
            color="success" 
            variant="flat"
            :loading="loadingAutorizacaoOrdem"
            @click="executarAutorizacaoOrdem"
          >
            <v-icon start>mdi-check-all</v-icon>
            Autorizar Todas ({{ pendenciasOrdemSelecionada.length }})
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ✅ DIALOG DE LOADING MELHORADO -->
    <v-dialog v-model="loadingDialog" persistent max-width="800">
      <v-card>
        <!-- Header do Loading -->
        <v-card-title class="bg-gradient-primary text-white pa-4">
          <v-icon class="mr-3">mdi-cog-sync</v-icon>
          <div class="flex-grow-1">
            <div class="text-h6 font-weight-bold">Processando Autorizações</div>
            <div class="text-body-2 opacity-90">
              Ordem SAP: {{ ordemSelecionadaParaAutorizar || 'Individual' }}
            </div>
          </div>
          <v-chip color="white" text-color="primary" class="font-weight-bold">
            {{ autorizacoesProcessadas }} / {{ totalAutorizacoes }}
          </v-chip>
        </v-card-title>

        <v-card-text class="pa-0">
          <!-- Progress Bar Geral -->
          <v-progress-linear
            :model-value="progressoGeral"
            height="8"
            color="success"
            bg-color="grey-lighten-3"
            striped
            class="mb-0"
          ></v-progress-linear>

          <!-- Status Atual -->
          <div class="pa-4 bg-grey-lighten-5 border-b">
            <div class="d-flex align-center">
              <v-progress-circular
                v-if="statusAtual !== 'concluido'"
                indeterminate
                color="primary"
                size="24"
                width="3"
                class="mr-3"
              ></v-progress-circular>
              <v-icon v-else color="success" class="mr-3">mdi-check-circle</v-icon>
              
              <div class="flex-grow-1">
                <div class="text-body-1 font-weight-bold">{{ mensagemStatusAtual }}</div>
                <div class="text-body-2 text-grey">{{ submensagemStatus }}</div>
              </div>
            </div>
          </div>

          <!-- Lista de Autorizações -->
          <div class="pa-4" style="max-height: 400px; overflow-y: auto;">
            <v-list density="compact" class="pa-0">
              <v-list-item
                v-for="(item, index) in listaAutorizacoes"
                :key="item.id"
                class="pa-3 mb-2 rounded"
                :class="getItemClass(item.status)"
              >
                <template v-slot:prepend>
                  <!-- Ícone de Status -->
                  <v-avatar size="32" :color="getStatusColor(item.status)" class="mr-3">
                    <v-icon color="white" size="16">{{ getStatusIcon(item.status) }}</v-icon>
                  </v-avatar>
                </template>

                <div class="flex-grow-1">
                  <!-- Linha Principal -->
                  <div class="d-flex align-center mb-1">
                    <v-chip size="x-small" color="blue" class="mr-2">
                      ID: {{ item.id }}
                    </v-chip>
                    <div class="text-body-2 font-weight-bold flex-grow-1">
                      {{ item.parte }} - {{ item.tamanho }}
                    </div>
                    <v-chip size="x-small" :color="getStatusColor(item.status)" variant="flat">
                      {{ getStatusText(item.status) }}
                    </v-chip>
                  </div>
                  
                  <!-- Linha Secundária -->
                  <div class="text-body-2 text-grey">
                    {{ item.movimentacao }} • Qtd: {{ item.quantidade }}
                    <span v-if="item.tempo" class="ml-2">• {{ item.tempo }}</span>
                  </div>

                  <!-- Mensagem de Erro (se houver) -->
                  <div v-if="item.status === 'erro' && item.mensagemErro" class="text-caption text-error mt-1">
                    <v-icon size="12" class="mr-1">mdi-alert-circle</v-icon>
                    {{ item.mensagemErro }}
                  </div>
                </div>

                <template v-slot:append>
                  <!-- Indicador de Progresso -->
                  <div class="text-center ml-3">
                    <v-progress-circular
                      v-if="item.status === 'processando'"
                      indeterminate
                      color="primary"
                      size="20"
                      width="2"
                    ></v-progress-circular>
                    <v-icon
                      v-else-if="item.status === 'sucesso'"
                      color="success"
                      size="20"
                    >mdi-check-circle</v-icon>
                    <v-icon
                      v-else-if="item.status === 'erro'"
                      color="error"
                      size="20"
                    >mdi-close-circle</v-icon>
                    <div class="text-caption text-grey mt-1">
                      #{{ index + 1 }}
                    </div>
                  </div>
                </template>
              </v-list-item>
            </v-list>
          </div>

          <!-- Resumo Final (quando concluído) -->
          <div v-if="statusAtual === 'concluido'" class="pa-4 bg-grey-lighten-5 border-t">
            <v-row dense>
              <v-col cols="4">
                <div class="text-center">
                  <v-icon color="success" size="24" class="mb-1">mdi-check-circle</v-icon>
                  <div class="text-h6 font-weight-bold text-success">{{ sucessos }}</div>
                  <div class="text-caption text-grey">Autorizadas</div>
                </div>
              </v-col>
              <v-col cols="4">
                <div class="text-center">
                  <v-icon color="error" size="24" class="mb-1">mdi-close-circle</v-icon>
                  <div class="text-h6 font-weight-bold text-error">{{ erros }}</div>
                  <div class="text-caption text-grey">Falharam</div>
                </div>
              </v-col>
              <v-col cols="4">
                <div class="text-center">
                  <v-icon color="primary" size="24" class="mb-1">mdi-clock</v-icon>
                  <div class="text-h6 font-weight-bold text-primary">{{ tempoTotal }}s</div>
                  <div class="text-caption text-grey">Tempo Total</div>
                </div>
              </v-col>
            </v-row>
          </div>
        </v-card-text>

        <!-- Botões de Ação -->
        <v-card-actions class="pa-4" v-if="statusAtual === 'concluido'">
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            variant="flat"
            @click="fecharLoadingDialog"
          >
            <v-icon start>mdi-check</v-icon>
            Concluído
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { useDisplay } from 'vuetify'
import producaoService from '@/services/producaoService'

export default {
  name: 'PendenciasChefeDialog',
  
  props: {
    // Controle do dialog
    modelValue: {
      type: Boolean,
      default: false
    },
    
    // Dados das pendências
    pendencias: {
      type: Array,
      default: () => []
    },
    
    // Total de pendências
    totalPendencias: {
      type: Number,
      default: 0
    },
    
    // ID do chefe para autorização
    idChefe: {
      type: [Number, String],
      required: true
    },
    
    // Textos customizáveis
    titulo: {
      type: String,
      default: 'Pendências Não Autorizadas'
    },
    
    mensagemVazia: {
      type: String,
      default: 'Nenhuma pendência encontrada'
    },
    
    descricaoVazia: {
      type: String,
      default: 'Não há movimentações pendentes para autorizar.'
    }
  },
  
  emits: ['update:modelValue', 'pendencia-autorizada', 'fechar'],
  
  data() {
    return {
      // ✅ FILTRO SIMPLIFICADO
      filtroTexto: '',
      loadingFiltro: false,
      
      // Controle dos dialogs
      dialogConfirmarAutorizacao: false,
      dialogConfirmarAutorizacaoOrdem: false,
      loadingDialog: false,
      
      // Dados locais
      pendenciaSelecionada: null,
      loadingAutorizacaoLocal: null,
      loadingAutorizacaoOrdem: null,
      mensagemLoading: '',
      
      // Dados para autorização por ordem
      ordemSelecionadaParaAutorizar: null,
      pendenciasOrdemSelecionada: [],
      
      // Lista local de pendências (para manipulação)
      pendenciasLocais: [],

      // ✅ DADOS PARA O LOADING MELHORADO
      listaAutorizacoes: [],
      autorizacoesProcessadas: 0,
      totalAutorizacoes: 0,
      statusAtual: 'preparando',
      mensagemStatusAtual: '',
      submensagemStatus: '',
      sucessos: 0,
      erros: 0,
      tempoInicio: null,
      tempoTotal: 0,
    }
  },
  
  setup() {
    const { mobile } = useDisplay()
    return { mobile }
  },
  
  watch: {
    // Sincronizar pendências quando mudarem
    pendencias: {
      handler(novasPendencias) {
        this.pendenciasLocais = [...novasPendencias]
      },
      immediate: true
    },
    
    // Limpar filtros quando dialog fechar
    modelValue: {
      handler(novoValor) {
        if (!novoValor) {
          this.$nextTick(() => {
            this.resetarComponente()
          })
        }
      },
      immediate: false
    }
  },
  
  computed: {
    dialogVisible: {
      get() {
        return this.modelValue
      },
      set(value) {
        this.$emit('update:modelValue', value)
      }
    },
    
    totalPendenciasAtual() {
      return this.pendenciasLocais.length
    },
    
    progressoGeral() {
      if (this.totalAutorizacoes === 0) return 0
      return (this.autorizacoesProcessadas / this.totalAutorizacoes) * 100
    },
    
    // ✅ AGRUPAR PENDÊNCIAS POR ORDEM SAP
    pendenciasAgrupadas() {
      if (!this.pendenciasLocais || this.pendenciasLocais.length === 0) {
        return {}
      }

      const agrupadas = {}
      
      this.pendenciasLocais.forEach(pendencia => {
        // ✅ ACESSO CORRETO AO SAP
        let ordemSap = 'Sem Ordem'
        
        // Tentar diferentes caminhos para encontrar o SAP
        if (pendencia.ordem_info?.sap) {
          ordemSap = String(pendencia.ordem_info.sap)
        } else if (pendencia.ordem_producao?.id_ordem_sap) {
          ordemSap = String(pendencia.ordem_producao.id_ordem_sap)
        } else if (pendencia.sap) {
          ordemSap = String(pendencia.sap)
        } else if (pendencia.ordem_sap) {
          ordemSap = String(pendencia.ordem_sap)
        }
        
        if (!agrupadas[ordemSap]) {
          agrupadas[ordemSap] = []
        }
        
        agrupadas[ordemSap].push(pendencia)
      })

      return agrupadas
    },

    // ✅ PENDÊNCIAS FILTRADAS POR TEXTO
    pendenciasAgrupadasFiltradas() {
      if (!this.filtroTexto || this.filtroTexto.trim() === '') {
        return this.pendenciasAgrupadas
      }

      const termo = this.filtroTexto.toLowerCase().trim()
      const filtradas = {}

      Object.keys(this.pendenciasAgrupadas).forEach(ordemSap => {
        const pendenciasOrdem = this.pendenciasAgrupadas[ordemSap]
        const primeiraP = pendenciasOrdem[0]
        
        // ✅ BUSCAR EM MÚLTIPLOS CAMPOS
        const cliente = primeiraP?.cliente || primeiraP?.ordem_producao?.detalhes?.cliente?.nome || ''
        const modelo = primeiraP?.modelo || primeiraP?.ordem_producao?.detalhes?.modelo || ''
        
        // Verificar se corresponde ao filtro
        const corresponde = 
          ordemSap.toLowerCase().includes(termo) ||
          cliente.toLowerCase().includes(termo) ||
          modelo.toLowerCase().includes(termo)
        
        if (corresponde) {
          filtradas[ordemSap] = pendenciasOrdem
        }
      })

      return filtradas
    },

    // ✅ PENDÊNCIAS FILTRADAS E ORDENADAS
    pendenciasAgrupadasOrdenadas() {
      const agrupadas = { ...this.pendenciasAgrupadasFiltradas }
      
      // Ordenar cada grupo de pendências
      Object.keys(agrupadas).forEach(ordemSap => {
        agrupadas[ordemSap] = this.ordenarPendenciasPorParteETamanho(agrupadas[ordemSap])
      })
      
      return agrupadas
    },
    
    // Total de pendências filtradas
    totalPendenciasFiltradas() {
      return Object.values(this.pendenciasAgrupadasOrdenadas)
        .reduce((total, pendencias) => total + pendencias.length, 0)
    }
  },
  
  methods: {
    handleDialogChange(value) {
      if (!value) {
        this.fecharDialog()
      }
    },
    
    fecharDialog() {
      // Reset completo do estado do componente
      this.resetarComponente()
      
      // Emitir eventos
      this.$emit('update:modelValue', false)
      this.$emit('fechar')
      
      console.log('✅ Componente fechado e filtros limpos')
    },
    
    // ✅ MÉTODO PARA RESET COMPLETO
    resetarComponente() {
      this.filtroTexto = ''
      this.loadingFiltro = false
      this.pendenciaSelecionada = null
      this.loadingAutorizacaoLocal = null
      this.loadingAutorizacaoOrdem = null
      this.ordemSelecionadaParaAutorizar = null
      this.pendenciasOrdemSelecionada = []
      this.dialogConfirmarAutorizacao = false
      this.dialogConfirmarAutorizacaoOrdem = false
      this.loadingDialog = false
      this.mensagemLoading = ''
      
      // Reset dos dados de loading
      this.listaAutorizacoes = []
      this.autorizacoesProcessadas = 0
      this.totalAutorizacoes = 0
      this.statusAtual = 'preparando'
      this.mensagemStatusAtual = ''
      this.submensagemStatus = ''
      this.sucessos = 0
      this.erros = 0
      this.tempoInicio = null
      this.tempoTotal = 0
    },

    // ✅ ORDENAÇÃO POR PARTE E DEPOIS TAMANHO
    ordenarPendenciasPorParteETamanho(pendencias) {
      return pendencias.slice().sort((a, b) => {
        // 1º Critério: Ordenar por PARTE
        const parteA = String(a.item?.parte || a.parte || '').trim().toUpperCase()
        const parteB = String(b.item?.parte || b.parte || '').trim().toUpperCase()
        
        const comparacaoParte = parteA.localeCompare(parteB, 'pt-BR', { 
          numeric: true, 
          sensitivity: 'base' 
        })
        
        // Se as partes são diferentes, usar essa ordenação
        if (comparacaoParte !== 0) {
          return comparacaoParte
        }
        
        // 2º Critério: Se partes iguais, ordenar por TAMANHO
        const tamanhoA = String(a.item?.tamanho || a.tamanho || '').trim()
        const tamanhoB = String(b.item?.tamanho || b.tamanho || '').trim()
        
        return this.compararTamanhosInteligente(tamanhoA, tamanhoB)
      })
    },

    // ✅ COMPARAÇÃO INTELIGENTE DE TAMANHOS
    compararTamanhosInteligente(tamanhoA, tamanhoB) {
      // Converter para string e limpar
      const a = String(tamanhoA).trim().toUpperCase()
      const b = String(tamanhoB).trim().toUpperCase()
      
      // Se ambos são números, comparar numericamente
      const numA = parseFloat(a)
      const numB = parseFloat(b)
      
      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB
      }
      
      // Se um é número e outro não, números vêm primeiro
      if (!isNaN(numA) && isNaN(numB)) return -1
      if (isNaN(numA) && !isNaN(numB)) return 1
      
      // Para strings, usar ordenação natural
      return a.localeCompare(b, 'pt-BR', { 
        numeric: true, 
        sensitivity: 'base' 
      })
    },

    // ✅ FILTRAR PENDÊNCIAS EM TEMPO REAL
    filtrarPendencias() {
      this.loadingFiltro = true
      
      // Simular delay de busca para UX
      setTimeout(() => {
        this.loadingFiltro = false
        
        // Log para debug
        const termo = this.filtroTexto?.toLowerCase().trim() || ''
        const resultados = Object.keys(this.pendenciasAgrupadasOrdenadas).length
        
        console.log(`🔍 Filtro aplicado: "${termo}"`)
        console.log(`📋 Encontradas ${resultados} ordens com ${this.totalPendenciasFiltradas} pendências`)
        
        if (termo && resultados > 0) {
          console.log('✅ Ordens encontradas:', Object.keys(this.pendenciasAgrupadasOrdenadas))
        }
      }, 300)
    },

    // ✅ LIMPAR FILTRO
    limparFiltro() {
      this.filtroTexto = ''
      this.loadingFiltro = false
      console.log('🗑️ Filtro limpo')
    },

    // Abrir dialog de confirmação individual
    confirmarAutorizacao(pendencia) {
      this.pendenciaSelecionada = pendencia
      this.dialogConfirmarAutorizacao = true
    },
    
    // Confirmar autorização por ordem
    confirmarAutorizacaoOrdem(ordemSap, pendenciasOrdem) {
      this.ordemSelecionadaParaAutorizar = ordemSap
      this.pendenciasOrdemSelecionada = [...pendenciasOrdem]
      this.dialogConfirmarAutorizacaoOrdem = true
    },

    // ✅ PREPARAR LISTA DE AUTORIZAÇÕES
    prepararListaAutorizacoes(pendencias) {
      this.listaAutorizacoes = pendencias.map((pendencia, index) => ({
        id: pendencia.id,
        parte: pendencia.item?.parte || 'N/A',
        tamanho: pendencia.item?.tamanho || 'N/A',
        quantidade: pendencia.item?.quantidade || 0,
        movimentacao: `${pendencia.movimentacao?.de || 'N/A'} → ${pendencia.movimentacao?.para || 'N/A'}`,
        status: 'aguardando',
        mensagemErro: null,
        tempo: null
      }))
      
      this.totalAutorizacoes = pendencias.length
      this.autorizacoesProcessadas = 0
      this.sucessos = 0
      this.erros = 0
      this.tempoInicio = Date.now()
    },

    // ✅ ATUALIZAR STATUS DE UM ITEM
    atualizarStatusItem(id, status, mensagemErro = null) {
      const item = this.listaAutorizacoes.find(i => i.id === id)
      if (item) {
        item.status = status
        item.mensagemErro = mensagemErro
        
        if (status === 'sucesso' || status === 'erro') {
          this.autorizacoesProcessadas++
          item.tempo = `${((Date.now() - this.tempoInicio) / 1000).toFixed(1)}s`
          
          if (status === 'sucesso') {
            this.sucessos++
          } else {
            this.erros++
          }
        }
      }
    },
    
    // ✅ ATUALIZAR STATUS GERAL
    atualizarStatusGeral(status, mensagem, submensagem = '') {
      this.statusAtual = status
      this.mensagemStatusAtual = mensagem
      this.submensagemStatus = submensagem
    },
    
    // ✅ MÉTODOS PARA ESTILIZAÇÃO
    getItemClass(status) {
      switch (status) {
        case 'processando': return 'bg-blue-lighten-5 border-l-4 border-blue'
        case 'sucesso': return 'bg-green-lighten-5 border-l-4 border-green'
        case 'erro': return 'bg-red-lighten-5 border-l-4 border-red'
        default: return 'bg-grey-lighten-4'
      }
    },
    
    getStatusColor(status) {
      switch (status) {
        case 'processando': return 'blue'
        case 'sucesso': return 'success'
        case 'erro': return 'error'
        default: return 'grey'
      }
    },
    
    getStatusIcon(status) {
      switch (status) {
        case 'processando': return 'mdi-cog-sync'
        case 'sucesso': return 'mdi-check'
        case 'erro': return 'mdi-close'
        default: return 'mdi-clock-outline'
      }
    },
    
    getStatusText(status) {
      switch (status) {
        case 'aguardando': return 'Aguardando'
        case 'processando': return 'Processando'
        case 'sucesso': return 'Autorizada'
        case 'erro': return 'Falhou'
        default: return 'Pendente'
      }
    },
    
    // ✅ FECHAR DIALOG DE LOADING
    fecharLoadingDialog() {
      this.loadingDialog = false
      this.tempoTotal = ((Date.now() - this.tempoInicio) / 1000).toFixed(1)
      
      // Fechar dialog principal se não há mais pendências
      if (this.pendenciasLocais.length === 0) {
        this.fecharDialog()
      }
    },

    // ✅ FORMATAR DATA PARA EXIBIÇÃO
    formatarDataPendencia(data) {
      if (!data) return 'N/A'
      
      try {
        const dataObj = new Date(data)
        return dataObj.toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch (error) {
        return 'Data inválida'
      }
    },
    
    // ✅ EXECUTAR AUTORIZAÇÃO INDIVIDUAL
    async executarAutorizacao() {
      if (!this.pendenciaSelecionada) return
      
      try {
        this.dialogConfirmarAutorizacao = false
        this.prepararListaAutorizacoes([this.pendenciaSelecionada])
        this.loadingDialog = true
        this.loadingAutorizacaoLocal = this.pendenciaSelecionada.id
        
        this.atualizarStatusGeral(
          'processando',
          'Autorizando movimentação individual...',
          `ID: ${this.pendenciaSelecionada.id} - ${this.pendenciaSelecionada.item?.parte || 'N/A'}`
        )
        
        this.atualizarStatusItem(this.pendenciaSelecionada.id, 'processando')
        
        const response = await producaoService.autorizarPendencia(
          this.pendenciaSelecionada.id, 
          this.idChefe
        )
        
        console.log('Resposta da autorização:', response)
        
        this.atualizarStatusItem(this.pendenciaSelecionada.id, 'sucesso')
        
        this.pendenciasLocais = this.pendenciasLocais.filter(
          p => p.id !== this.pendenciaSelecionada.id
        )
        
        this.$emit('pendencia-autorizada', this.pendenciaSelecionada)
        
        this.atualizarStatusGeral(
          'concluido',
          'Autorização concluída com sucesso!',
          'Movimentação autorizada e processada'
        )
        
        console.log('Movimentação autorizada com sucesso!')
        
      } catch (error) {
        console.error('Erro ao autorizar pendência:', error)
        
        this.atualizarStatusItem(
          this.pendenciaSelecionada.id, 
          'erro', 
          error.message || 'Erro desconhecido'
        )
        
        this.atualizarStatusGeral(
          'concluido',
          'Erro na autorização',
          'Falha ao processar a movimentação'
        )
        
        console.log('Erro ao autorizar movimentação. Tente novamente.')
      } finally {
        this.loadingAutorizacaoLocal = null
        this.pendenciaSelecionada = null
      }
    },

    // ✅ EXECUTAR AUTORIZAÇÃO POR ORDEM (MELHORADO)
    async executarAutorizacaoOrdem() {
      if (!this.pendenciasOrdemSelecionada.length) return
      
      try {
        // Fechar dialog de confirmação
        this.dialogConfirmarAutorizacaoOrdem = false
        
        // ✅ PREPARAR INTERFACE DE LOADING
        this.prepararListaAutorizacoes(this.pendenciasOrdemSelecionada)
        this.loadingDialog = true
        this.loadingAutorizacaoOrdem = this.ordemSelecionadaParaAutorizar
        
        this.atualizarStatusGeral(
          'preparando',
          'Preparando autorizações em lote...',
          `${this.pendenciasOrdemSelecionada.length} pendências para processar`
        )
        
        // Pausa inicial
        await new Promise(resolve => setTimeout(resolve, 1500))
        
        // ✅ PROCESSAR CADA PENDÊNCIA
        for (let i = 0; i < this.pendenciasOrdemSelecionada.length; i++) {
          const pendencia = this.pendenciasOrdemSelecionada[i]
          
          try {
            // ✅ ATUALIZAR STATUS GERAL
            this.atualizarStatusGeral(
              'processando',
              `Processando ${i + 1} de ${this.pendenciasOrdemSelecionada.length}`,
              `ID: ${pendencia.id} - ${pendencia.item?.parte || 'N/A'} - ${pendencia.item?.tamanho || 'N/A'}`
            )
            
            // ✅ ATUALIZAR STATUS DO ITEM
            this.atualizarStatusItem(pendencia.id, 'processando')
            
            // Chamar API individual
            const response = await producaoService.autorizarPendencia(pendencia.id, this.idChefe)
            
            console.log(`Pendência ${pendencia.id} autorizada:`, response)
            
            // ✅ SUCESSO
            this.atualizarStatusItem(pendencia.id, 'sucesso')
            
            // Remover da lista local imediatamente
            this.pendenciasLocais = this.pendenciasLocais.filter(p => p.id !== pendencia.id)
            
            // Emitir evento individual
            this.$emit('pendencia-autorizada', pendencia)
            
            // Pausa para feedback visual
            await new Promise(resolve => setTimeout(resolve, 800))
            
          } catch (error) {
            console.error(`Erro ao autorizar pendência ${pendencia.id}:`, error)
            
            // ✅ ERRO
            this.atualizarStatusItem(
              pendencia.id, 
              'erro', 
              error.message || 'Falha na autorização'
            )
            
            // Continuar com as próximas mesmo se uma falhar
            continue
          }
        }
        
        // ✅ FINALIZAR PROCESSO
        this.tempoTotal = ((Date.now() - this.tempoInicio) / 1000).toFixed(1)
        
        this.atualizarStatusGeral(
          'concluido',
          'Processo de autorização concluído!',
          `${this.sucessos} autorizadas, ${this.erros} falharam em ${this.tempoTotal}s`
        )
        
        // Mensagens de resultado
        if (this.sucessos > 0) {
          console.log(`✅ ${this.sucessos} movimentações autorizadas com sucesso!`)
        }
        
        if (this.erros > 0) {
          console.log(`⚠️ ${this.erros} movimentações falharam na autorização.`)
        }
        
      } catch (error) {
        console.error('Erro geral ao autorizar pendências da ordem:', error)
        
        this.atualizarStatusGeral(
          'concluido',
          'Erro crítico no processo',
          'Falha geral na autorização em lote'
        )
        
        console.log('Erro ao processar autorizações. Tente novamente.')
      } finally {
        this.loadingAutorizacaoOrdem = null
        this.ordemSelecionadaParaAutorizar = null
        this.pendenciasOrdemSelecionada = []
      }
    }
  }
}
</script>

<style scoped>
/* ✅ GRADIENTE CINZA ELEGANTE */
.bg-gradient-dark {
  background: linear-gradient(135deg, #37474f 0%, #455a64 50%, #546e7a 100%);
  box-shadow: 0 4px 20px rgba(55, 71, 79, 0.3);
  position: relative;
  overflow: hidden;
}

.bg-gradient-dark::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%);
  pointer-events: none;
}

.bg-gradient-primary {
  background: linear-gradient(135deg, #1976d2 0%, #1e88e5 50%, #2196f3 100%);
}

.hover-row:hover {
  background-color: rgba(0, 0, 0, 0.02);
  transition: background-color 0.2s ease;
}

.sticky-top {
  position: sticky;
  top: 0;
  z-index: 10;
}

.border-b {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.border-t {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}

/* ✅ ESTILOS PARA O FILTRO DE TEXTO NO HEADER */
.filtro-text-header {
  border-radius: 12px;
}

.filtro-text-header .v-field {
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.filtro-text-header .v-field:hover {
  border-color: rgba(255, 255, 255, 0.4);
  background-color: rgba(255, 255, 255, 0.1);
  transform: translateY(-1px);
}

.filtro-text-header .v-field--focused {
  border-color: white;
  background-color: rgba(255, 255, 255, 0.15);
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.2);
}

.filtro-text-header .v-field__input {
  color: white;
  font-weight: 500;
}

.filtro-text-header .v-field__input::placeholder {
  color: rgba(255, 255, 255, 0.7);
}

.filtro-text-header .v-icon {
  color: rgba(255, 255, 255, 0.8);
}

.filtro-text-header .v-field__clearable .v-icon {
  color: rgba(255, 255, 255, 0.6);
}

.filtro-text-header .v-field__clearable .v-icon:hover {
  color: white;
}

/* ✅ HIGHLIGHT PARA TERMOS DE BUSCA */
.highlight-search {
  background: rgba(255, 255, 255, 0.2);
  padding: 2px 4px;
  border-radius: 4px;
  font-weight: bold;
}

/* ✅ BOTÕES E CHIPS MELHORADOS */
.v-chip.font-weight-bold {
  font-weight: 600;
  letter-spacing: 0.5px;
  transition: all 0.2s ease;
}

.v-btn.border-white {
  border-color: rgba(255, 255, 255, 0.4) !important;
  transition: all 0.3s ease;
}

.v-btn.border-white:hover {
  border-color: white !important;
  background-color: rgba(255, 255, 255, 0.1);
  transform: translateY(-1px);
}

/* ✅ SCROLLBAR PERSONALIZADA */
.v-card-text::-webkit-scrollbar {
  width: 6px;
}

.v-card-text::-webkit-scrollbar-track {
  background: #f5f5f5;
  border-radius: 3px;
}

.v-card-text::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #37474f, #546e7a);
  border-radius: 3px;
}

.v-card-text::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #263238, #37474f);
}

/* ✅ ANIMAÇÕES SUAVES */
.gap-2 {
  gap: 8px;
}

.v-chip, .v-btn {
  transition: all 0.2s ease;
}

.v-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

/* ✅ CARDS COM SOMBRA MELHORADA */
.elevation-4 {
  box-shadow: 0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1) !important;
  transition: all 0.3s ease;
}

.elevation-4:hover {
  box-shadow: 0 8px 24px rgba(0,0,0,0.2), 0 4px 8px rgba(0,0,0,0.15) !important;
  transform: translateY(-2px);
}

/* ✅ ESTILO PARA CHIPS DE STATUS */
.v-chip[color="success"] {
  background: linear-gradient(135deg, #4caf50, #66bb6a) !important;
}

.v-chip[color="primary"] {
  background: linear-gradient(135deg, #2196f3, #42a5f5) !important;
}

.v-chip[color="blue-darken-1"] {
  background: linear-gradient(135deg, #1976d2, #1e88e5) !important;
}

/* ✅ ESTILOS PARA O DIALOG DE LOADING */
.border-l-4 {
  border-left: 4px solid;
}

.border-blue {
  border-left-color: #2196f3;
}

.border-green {
  border-left-color: #4caf50;
}

.border-red {
  border-left-color: #f44336;
}

/* ✅ ANIMAÇÃO DE LOADING PERSONALIZADA */
.v-progress-circular {
  filter: drop-shadow(0 2px 4px rgba(0,0,0,0.2));
}

/* ✅ MELHORIAS PARA MOBILE */
@media (max-width: 768px) {
  .filtro-text-header .v-field {
    border-radius: 8px;
  }
  
  .gap-2 {
    gap: 4px;
  }
  
  .v-chip {
    font-size: 0.75rem;
  }
}

/* ✅ MELHORIAS ADICIONAIS */
.info-group {
  margin-bottom: 16px;
}

.info-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
}

.info-value {
  font-size: 0.875rem;
  font-weight: 500;
  color: #333;
  display: flex;
  align-items: center;
}

/* ✅ BOTÕES ARREDONDADOS */
.v-btn.rounded-lg {
  border-radius: 12px !important;
}

/* ✅ ESPAÇAMENTO CONSISTENTE */
.v-card-actions .v-btn {
  min-width: 140px;
  height: 44px;
}

/* ✅ CHIP PROCESSAR COM GRADIENTE BONITO */
.chip-processar {
  background: linear-gradient(135deg, #4caf50 0%, #66bb6a 50%, #81c784 100%) !important;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
  transition: all 0.3s ease;
  cursor: pointer;
}

.chip-processar:hover {
  background: linear-gradient(135deg, #43a047 0%, #5cb85c 50%, #7cb342 100%) !important;
  box-shadow: 0 6px 16px rgba(76, 175, 80, 0.4);
  transform: translateY(-2px);
}

.chip-processar:active {
  transform: translateY(0px);
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);
}

/* ✅ ESTILOS PARA DICAS E FEEDBACK */
.text-caption {
  opacity: 0.8;
}

/* ✅ ANIMAÇÃO PARA HIGHLIGHT */
@keyframes highlight {
  0% { background-color: rgba(255, 255, 255, 0.1); }
  50% { background-color: rgba(255, 255, 255, 0.3); }
  100% { background-color: rgba(255, 255, 255, 0.2); }
}

.highlight-search {
  animation: highlight 2s ease-in-out;
} 
</style>